import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { VendorStatusPill, getDisplayStatus } from '@/components/VendorStatusPill';
import { getVendors, createVendor } from '@/lib/api';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Search, Plus, ChevronRight, Users, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isEnterprise = user?.plan_tier === 'enterprise';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [newVendor, setNewVendor] = useState({ name: '', vendor_type: 'general', email: '', phone: '', contact_person: '', custom_vendor_type: '' });
  const navigate = useNavigate();

  const loadVendors = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.vendor_type = typeFilter;
      const { data } = await getVendors(params);
      setVendors(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadVendors(); }, [search, statusFilter, typeFilter]);

  const handleCreate = async () => {
    try {
      // Use custom vendor type if "other" is selected
      const vendorData = {...newVendor};
      if (vendorData.vendor_type === 'other' && vendorData.custom_vendor_type) {
        vendorData.vendor_type = vendorData.custom_vendor_type.toLowerCase().replace(/\s+/g, '_');
      }
      delete vendorData.custom_vendor_type; // Remove helper field
      
      await createVendor(vendorData);
      toast.success('Vendor created successfully');
      setShowCreate(false);
      setNewVendor({ name: '', vendor_type: 'general', email: '', phone: '', contact_person: '', custom_vendor_type: '' });
      loadVendors();
    } catch (err) {
      toast.error('Failed to create vendor');
    }
  };

  const handleDeleteVendor = async (vendorId, vendorName) => {
    if (!window.confirm(`Delete "${vendorName}"? This will remove all property assignments and documents. This action cannot be undone.`)) return;
    try {
      await api.delete(`/vendors/${vendorId}`);
      toast.success('Vendor deleted');
      loadVendors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  return (
    <div data-testid="vendors-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Vendors</h1>
          <p className="text-sm text-muted-foreground">{total} vendors total</p>
        </div>
        <div className="flex gap-2">
          {isEnterprise && (
            <Button variant="outline" data-testid="vendors-bulk-vendor-paid-button" onClick={async () => {
              const allPaid = vendors.every(v => v.vendor_paid);
              try {
                await api.post('/vendors/bulk-vendor-paid', { vendor_paid: !allPaid });
                toast.success(allPaid ? 'All vendors set to PM-paid' : 'All vendors set to Vendor-paid');
                loadVendors();
              } catch (err) { toast.error(err.response?.data?.detail?.message || 'Failed'); }
            }}>
              <DollarSign className="mr-2 h-4 w-4" /> {vendors.every(v => v.vendor_paid) ? 'Set All PM-Paid' : 'Set All Vendor-Paid'}
            </Button>
          )}
          <Button data-testid="vendors-create-button" onClick={() => setShowCreate(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Vendor
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="rounded-xl border border-border shadow-[var(--shadow-soft)]">
        <CardContent className="p-4">
          <div data-testid="portfolio-filter-bar" className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="vendors-search-input"
                placeholder="Search vendors..."
                className="pl-9"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="vendors-status-filter-select" className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="compliant">Compliant</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="vendors-type-filter-select" className="w-[160px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="plumber">Plumber</SelectItem>
                <SelectItem value="electrician">Electrician</SelectItem>
                <SelectItem value="hvac">HVAC</SelectItem>
                <SelectItem value="landscaping">Landscaping</SelectItem>
                <SelectItem value="cleaning">Cleaning</SelectItem>
                <SelectItem value="roofing">Roofing</SelectItem>
                <SelectItem value="cite">Cite</SelectItem>
                <SelectItem value="pest_control">Pest Control</SelectItem>
                <SelectItem value="pool">Pool</SelectItem>
                <SelectItem value="security">Security</SelectItem>
                <SelectItem value="waste_management">Waste Management</SelectItem>
                <SelectItem value="painting">Painting</SelectItem>
                <SelectItem value="general">General</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-xl border border-border shadow-[var(--shadow-soft)]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 rounded" />)}
            </div>
          ) : vendors.length === 0 ? (
            <div className="py-16 text-center">
              <Users className="mx-auto h-12 w-12 text-muted-foreground/40 mb-4" />
              <p className="text-muted-foreground">No vendors found</p>
              <Button variant="outline" className="mt-4" onClick={() => setShowCreate(true)}>Add your first vendor</Button>
            </div>
          ) : (
            <Table data-testid="vendors-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Status</TableHead>
                  {isEnterprise && <TableHead className="text-center">Vendor-Paid</TableHead>}
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vendors.map(v => (
                  <TableRow key={v.id} className="hover:bg-muted/50 transition-colors duration-150">
                    <TableCell className="cursor-pointer" onClick={() => navigate(`/vendors/${v.id}`)}>
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-medium shrink-0">
                          {v.name?.[0]}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{v.name}</p>
                          <p className="text-xs text-muted-foreground">{v.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm capitalize cursor-pointer" onClick={() => navigate(`/vendors/${v.id}`)}>{v.vendor_type?.replace('_', ' ')}</TableCell>
                    <TableCell className="text-sm cursor-pointer" onClick={() => navigate(`/vendors/${v.id}`)}>{v.contact_person || '-'}</TableCell>
                    <TableCell className="cursor-pointer" onClick={() => navigate(`/vendors/${v.id}`)}>
                      <span className="text-sm font-medium tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{v.compliance_score}%</span>
                    </TableCell>
                    <TableCell className="cursor-pointer" onClick={() => navigate(`/vendors/${v.id}`)}><VendorStatusPill status={v.status} /></TableCell>
                    {isEnterprise && (
                      <TableCell className="text-center" onClick={e => e.stopPropagation()}>
                        <VendorPaidToggle vendorId={v.id} initialValue={v.vendor_paid || false} onChanged={loadVendors} />
                      </TableCell>
                    )}
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteVendor(v.id, v.name)} 
                        data-testid={`vendor-delete-${v.id}`}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Add Vendor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input data-testid="vendor-create-name-input" value={newVendor.name} onChange={e => setNewVendor(p => ({...p, name: e.target.value}))} /></div>
            <div>
              <Label>Type</Label>
              <Select value={newVendor.vendor_type} onValueChange={v => setNewVendor(p => ({...p, vendor_type: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['general','plumber','electrician','hvac','landscaping','cleaning','roofing','pest_control','pool','security','waste_management','painting','cite','other'].map(t =>
                    <SelectItem key={t} value={t}>{t === 'other' ? 'Other (Custom)' : t.replace('_',' ')}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {newVendor.vendor_type === 'other' && (
              <div>
                <Label>Specify Vendor Type *</Label>
                <Input 
                  placeholder="e.g., Carpenter, Mason, etc."
                  value={newVendor.custom_vendor_type} 
                  onChange={e => setNewVendor(p => ({...p, custom_vendor_type: e.target.value}))}
                  data-testid="vendor-custom-type-input"
                />
              </div>
            )}
            <div><Label>Email</Label><Input data-testid="vendor-create-email-input" value={newVendor.email} onChange={e => setNewVendor(p => ({...p, email: e.target.value}))} /></div>
            <div><Label>Phone</Label><Input value={newVendor.phone} onChange={e => setNewVendor(p => ({...p, phone: e.target.value}))} /></div>
            <div><Label>Contact Person</Label><Input value={newVendor.contact_person} onChange={e => setNewVendor(p => ({...p, contact_person: e.target.value}))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button data-testid="vendor-create-submit-button" onClick={handleCreate} disabled={!newVendor.name}>Create Vendor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function VendorPaidToggle({ vendorId, initialValue, onChanged }) {
  const [value, setValue] = useState(initialValue);
  const handleToggle = async (checked) => {
    setValue(checked);
    try {
      await api.put(`/vendors/${vendorId}/vendor-paid`, { vendor_paid: checked });
      toast.success(checked ? 'Vendor set to Vendor-Paid ($50/yr)' : 'Vendor set to PM-Paid');
      onChanged?.();
    } catch (err) {
      setValue(!checked);
      toast.error(err.response?.data?.detail?.message || 'Failed to update');
    }
  };
  return <Switch checked={value} onCheckedChange={handleToggle} />;
}
